import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Link,
} from '@mui/material';
import {
  Block as SuspendIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  PlayArrow as UnsuspendIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  Add as AddIcon,
  AutoAwesome as SeedIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Link as RouterLink } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const statusColor = (s) => {
  const m = {
    active: 'success',
    scheduled: 'info',
    pending_payment: 'warning',
    pending_approval: 'warning',
    suspended: 'error',
    rejected: 'error',
    expired: 'default',
    draft: 'default',
  };
  return m[s] || 'default';
};

const thumbUrl = (post) =>
  post?.fullMediaUrl ||
  post?.primaryMediaUrl ||
  post?.thumbnailMediaUrl ||
  (Array.isArray(post?.media) && post.media[0]?.url) ||
  '';

const TownPulseAdmin = () => {
  const [tab, setTab] = useState('demo');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [dialogMode, setDialogMode] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    townName: '',
    businessName: '',
    media: [],
  });
  const [uploading, setUploading] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [creating, setCreating] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [feedSettings, setFeedSettings] = useState({
    show_live_my_town: false,
    show_demo_my_town: true,
    enable_placement_my_town: true,
  });
  const fileRef = useRef(null);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/business-posts/settings`, {
        headers: authHeaders(),
      });
      if (res.data?.success && res.data.data) {
        setFeedSettings((s) => ({ ...s, ...res.data.data }));
      }
    } catch {
      /* optional */
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { tab, limit: 100, placement: 'my_town' };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const res = await axios.get(`${API_BASE_URL}/business-posts`, {
        params,
        headers: authHeaders(),
      });
      setRows(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load Town Pulse posts');
    } finally {
      setLoading(false);
    }
  }, [tab, statusFilter, search]);

  useEffect(() => {
    document.title = 'Town Pulse Screen - LOCSY';
    loadRows();
    loadSettings();
  }, [loadRows, loadSettings]);

  const saveFeedToggle = async (key, value) => {
    const next = { ...feedSettings, [key]: value };
    setFeedSettings(next);
    try {
      await axios.put(
        `${API_BASE_URL}/business-posts/settings`,
        { [key]: value },
        { headers: authHeaders() },
      );
      toast.success('Town Pulse feed setting saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save setting');
      loadSettings();
    }
  };

  const moderate = async (id, action, extra = {}) => {
    try {
      await axios.post(
        `${API_BASE_URL}/business-posts/${id}/moderate`,
        { action, notes: actionNotes, ...extra },
        { headers: authHeaders() },
      );
      toast.success(`Action: ${action}`);
      setSelected(null);
      setDialogMode(null);
      setActionNotes('');
      setAnalytics(null);
      loadRows();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    }
  };

  const openAction = (post, mode) => {
    setSelected(post);
    setDialogMode(mode);
    setAnalytics(null);
    setActionNotes(post.adminRemarks || '');
  };

  const openEdit = (post) => {
    setSelected(post);
    setEditForm({
      title: post.title || '',
      description: post.description || '',
      townName: post.townName || '',
      businessName: post.businessName || '',
      media: Array.isArray(post.media) ? [...post.media] : [],
    });
    setDialogMode('edit');
  };

  const openCreate = () => {
    setSelected(null);
    setEditForm({
      title: '',
      description: '',
      townName: 'Chintalapudi',
      businessName: 'Town Pulse Demo',
      media: [],
    });
    setDialogMode('create');
  };

  const loadAnalytics = async (post) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/business-posts/analytics/summary`, {
        params: { postId: post.id, businessId: post.businessId },
        headers: authHeaders(),
      });
      setAnalytics(res.data?.data || null);
      setSelected(post);
      setDialogMode('analytics');
    } catch {
      toast.error('Analytics failed');
    }
  };

  const uploadImage = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_BASE_URL}/business-posts/upload`, formData, {
        headers: authHeaders(),
      });
      const uploaded = response.data?.data;
      if (uploaded?.url) {
        setEditForm((f) => ({
          ...f,
          media: [{ url: uploaded.url, type: 'image', ...uploaded }],
        }));
        toast.success('Image uploaded — save to apply');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const saveEdit = async () => {
    if (!selected?.id) return;
    if (!editForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(
        `${API_BASE_URL}/business-posts/${selected.id}`,
        {
          title: editForm.title.trim(),
          description: editForm.description,
          townName: editForm.townName || undefined,
          businessName: editForm.businessName || undefined,
          media: editForm.media,
        },
        { headers: authHeaders() },
      );
      toast.success('Town Pulse ad updated');
      setDialogMode(null);
      setSelected(null);
      loadRows();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setSavingEdit(false);
    }
  };

  const createDemo = async () => {
    if (!editForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!editForm.media?.length) {
      toast.error('Upload an image for the reel');
      return;
    }
    setCreating(true);
    try {
      await axios.post(
        `${API_BASE_URL}/business-posts`,
        {
          postType: 'offer',
          title: editForm.title.trim(),
          description: editForm.description,
          townName: editForm.townName || 'Chintalapudi',
          businessName: editForm.businessName || 'Town Pulse Demo',
          media: editForm.media,
          placements: ['my_town'],
          isDemo: true,
          durationDays: 0,
        },
        { headers: authHeaders() },
      );
      toast.success('Demo Town Pulse ad created');
      setDialogMode(null);
      setTab('demo');
      loadRows();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const seedDemos = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/business-posts/admin/seed-town-pulse-demos`,
        { force: false },
        { headers: authHeaders() },
      );
      toast.success(res.data?.message || 'Demo ads seeded');
      setTab('demo');
      loadRows();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const preview = thumbUrl(
    dialogMode === 'edit' || dialogMode === 'create'
      ? { media: editForm.media, primaryMediaUrl: editForm.media[0]?.url }
      : selected,
  );

  return (
    <DashboardLayout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          spacing={1}
          mb={1}
        >
          <Box>
            <Typography variant="h5" fontWeight={800}>
              Town Pulse Screen
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Control citizen Town Pulse reels — update image/text, pause, delete, and track views.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              component={RouterLink}
              to="/content/ad-settings"
              startIcon={<SettingsIcon />}
              size="small"
            >
              Feed settings
            </Button>
            <Button
              variant="outlined"
              startIcon={<SeedIcon />}
              onClick={seedDemos}
              disabled={seeding}
              size="small"
            >
              {seeding ? 'Seeding…' : 'Seed sample demos'}
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} size="small">
              Add demo ad
            </Button>
          </Stack>
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          Path: <strong>/content/townpulsescreen</strong> · Only <code>my_town</code> placement posts.
          Pause removes from citizen app immediately. Live ads stay hidden until you enable them below.
        </Alert>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Citizen feed visibility
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(feedSettings.enable_placement_my_town)}
                  onChange={(e) => saveFeedToggle('enable_placement_my_town', e.target.checked)}
                />
              }
              label="Enable Town Pulse placement"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(feedSettings.show_demo_my_town)}
                  onChange={(e) => saveFeedToggle('show_demo_my_town', e.target.checked)}
                />
              }
              label="Show Demo ads"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(feedSettings.show_live_my_town)}
                  onChange={(e) => saveFeedToggle('show_live_my_town', e.target.checked)}
                />
              }
              label="Show Live ads"
            />
          </Stack>
        </Paper>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab value="demo" label="Demo reels" />
          <Tab value="live" label="Live reels" />
        </Tabs>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
          <TextField
            size="small"
            placeholder="Search title or business…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadRows()}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="suspended">Paused</MenuItem>
              <MenuItem value="pending_approval">Pending approval</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
            </Select>
          </FormControl>
          <Button startIcon={<RefreshIcon />} onClick={loadRows}>
            Refresh
          </Button>
        </Stack>

        {loading ? (
          <Typography>Loading…</Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Preview</TableCell>
                  <TableCell>Business / Town</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Views</TableCell>
                  <TableCell align="right">Clicks</TableCell>
                  <TableCell align="right">CTR</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Alert severity="info">
                        No Town Pulse posts in this tab.{' '}
                        {tab === 'demo' && (
                          <Link component="button" onClick={seedDemos}>
                            Seed sample demos
                          </Link>
                        )}
                      </Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => {
                    const img = thumbUrl(r);
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell>
                          {img ? (
                            <Box
                              component="img"
                              src={img}
                              alt=""
                              sx={{
                                width: 56,
                                height: 72,
                                objectFit: 'cover',
                                borderRadius: 1,
                                bgcolor: 'grey.100',
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 56,
                                height: 72,
                                borderRadius: 1,
                                bgcolor: 'grey.200',
                                display: 'grid',
                                placeItems: 'center',
                              }}
                            >
                              <ImageIcon fontSize="small" color="disabled" />
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            {r.businessName || r.businessId || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.townName || '—'}
                          </Typography>
                          {r.isDemo && (
                            <Chip
                              size="small"
                              label="Demo"
                              color="warning"
                              sx={{ ml: 0.5, height: 20 }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Typography variant="body2" noWrap title={r.title}>
                            {r.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={r.status} color={statusColor(r.status)} />
                        </TableCell>
                        <TableCell align="right">{r.totalViews ?? 0}</TableCell>
                        <TableCell align="right">{r.totalClicks ?? 0}</TableCell>
                        <TableCell align="right">
                          {r.ctr != null ? `${r.ctr}%` : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEdit(r)} title="Edit image / text">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => loadAnalytics(r)} title="Views & analytics">
                            <ViewIcon fontSize="small" />
                          </IconButton>
                          {r.status !== 'suspended' ? (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openAction(r, 'suspend')}
                              title="Pause (hide from citizen)"
                            >
                              <SuspendIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => moderate(r.id, 'unsuspend')}
                              title="Resume"
                            >
                              <UnsuspendIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => openAction(r, 'delete')}
                            title="Delete permanently"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog
          open={Boolean(dialogMode)}
          onClose={() => {
            setSelected(null);
            setDialogMode(null);
            setAnalytics(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {dialogMode === 'edit' && 'Edit Town Pulse ad'}
            {dialogMode === 'create' && 'Add demo Town Pulse ad'}
            {dialogMode === 'suspend' && 'Pause Town Pulse ad'}
            {dialogMode === 'delete' && 'Delete Town Pulse ad'}
            {dialogMode === 'analytics' && 'Views & analytics'}
          </DialogTitle>
          <DialogContent>
            {(dialogMode === 'edit' || dialogMode === 'create') && (
              <Stack spacing={2} sx={{ mt: 1 }}>
                {preview ? (
                  <Box
                    component="img"
                    src={preview}
                    alt="preview"
                    sx={{
                      width: '100%',
                      maxHeight: 220,
                      objectFit: 'cover',
                      borderRadius: 2,
                      bgcolor: 'grey.100',
                    }}
                  />
                ) : (
                  <Alert severity="warning">No image yet — upload a reel image.</Alert>
                )}
                <Button
                  variant="outlined"
                  startIcon={<ImageIcon />}
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                >
                  {uploading ? 'Uploading…' : 'Upload / replace image'}
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  hidden
                  onChange={(e) => uploadImage(e.target.files)}
                />
                <TextField
                  label="Title"
                  fullWidth
                  value={editForm.title}
                  onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                />
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={editForm.description}
                  onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                />
                <TextField
                  label="Business name"
                  fullWidth
                  value={editForm.businessName}
                  onChange={(e) => setEditForm((f) => ({ ...f, businessName: e.target.value }))}
                />
                <TextField
                  label="Town"
                  fullWidth
                  value={editForm.townName}
                  onChange={(e) => setEditForm((f) => ({ ...f, townName: e.target.value }))}
                  helperText="Used for distance sorting on Town Pulse"
                />
              </Stack>
            )}

            {dialogMode === 'analytics' && analytics && (
              <Stack spacing={1} sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>{selected?.title}</strong>
                </Typography>
                <Typography variant="body2">Views: {analytics.totalViews ?? 0}</Typography>
                <Typography variant="body2">Unique viewers: {analytics.uniqueViews ?? 0}</Typography>
                <Typography variant="body2">CTR: {analytics.ctr ?? 0}%</Typography>
                <Typography variant="body2">
                  Clicks: {JSON.stringify(analytics.clicksByType || {})}
                </Typography>
              </Stack>
            )}

            {(dialogMode === 'suspend' || dialogMode === 'delete') && (
              <Box sx={{ mt: 1 }}>
                <Alert severity={dialogMode === 'delete' ? 'error' : 'warning'} sx={{ mb: 2 }}>
                  {dialogMode === 'suspend'
                    ? 'Pausing hides this reel from the citizen Town Pulse screen immediately.'
                    : 'Permanent delete — cannot be undone.'}
                </Alert>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {selected?.businessName} — {selected?.title}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Note (optional)"
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setSelected(null);
                setDialogMode(null);
                setAnalytics(null);
              }}
            >
              Close
            </Button>
            {dialogMode === 'edit' && (
              <Button variant="contained" onClick={saveEdit} disabled={savingEdit}>
                {savingEdit ? 'Saving…' : 'Save changes'}
              </Button>
            )}
            {dialogMode === 'create' && (
              <Button variant="contained" onClick={createDemo} disabled={creating}>
                {creating ? 'Creating…' : 'Create demo'}
              </Button>
            )}
            {dialogMode === 'suspend' && selected && (
              <Button color="error" variant="contained" onClick={() => moderate(selected.id, 'suspend')}>
                Pause now
              </Button>
            )}
            {dialogMode === 'delete' && selected && (
              <Button color="error" variant="contained" onClick={() => moderate(selected.id, 'delete')}>
                Delete permanently
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default TownPulseAdmin;
